import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Badge, Alert } from 'react-bootstrap';
import Layout from '../components/layouts/Main';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { toast } from 'react-toastify';
import { PulseLoader } from "react-spinners";
import { FiFilter, FiDownload, FiSearch, FiFileText } from 'react-icons/fi';
import ReportDownloader from '../components/common/ReportDownloader';
import { useHasPermission, PERMISSIONS } from "../utils/permissions";

const ENTITIES = {
  SALES_ORDERS: 'Sales Orders',
  PURCHASE_ORDERS: 'Purchase Orders',
  TOTAL_INVENTORY: 'Total Inventory',
  PRODUCT_STOCK_OVERVIEW: 'Product Stock Overview'
};

const Reports = () => {
  const [selectedEntity, setSelectedEntity] = useState(ENTITIES.SALES_ORDERS);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    buyer: '',
    vendor: '',
    broker: '',
    product: '',
  });

  const [data, setData] = useState([]);
  const [products, setProducts] = useState([]);
  const [reservedStockMap, setReservedStockMap] = useState({});
  const [inProductionStockMap, setInProductionStockMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Lists for filter dropdowns
  const [buyers, setBuyers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [brokers, setBrokers] = useState([]);

  // Helper for consistent formatting in the preview table
  const formatValue = (val, header, key) => {
    if (val === true) return 'Yes';
    if (val === false) return 'No';
    if (val === undefined || val === null || val === '') return '-';

    // Date formatting (DD/MM/YYYY HH:mm for orderDate, DD/MM/YYYY for others)
    if (header.includes('Date') || header === 'EDD' || key.toLowerCase().includes('date') || key === 'expectedDeliveryDate') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        
        if (header.includes('Order Date') || key === 'orderDate') {
          const hours = String(d.getHours()).padStart(2, '0');
          const minutes = String(d.getMinutes()).padStart(2, '0');
          return `${day}/${month}/${year} ${hours}:${minutes}`;
        }
        return `${day}/${month}/${year}`;
      }
    }

    // Number formatting (2 decimal places for price/amount)
    if (typeof val === 'number' && (header.includes('Amount') || header.includes('Price') || header.includes('Total'))) {
      return val.toFixed(2);
    }

    return val;
  };

  // Permission checks
  const canViewReports = useHasPermission([PERMISSIONS.SO_VIEW, PERMISSIONS.PO_VIEW, PERMISSIONS.STOCK_VIEW]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [buyersRes, vendorsRes, brokersRes, productsRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/buyers`),
        axios.get(`${config.API_BASE_URL}/vendors`),
        axios.get(`${config.API_BASE_URL}/brokers`),
        axios.get(`${config.API_BASE_URL}/products?isActive=true`),
      ]);
      setBuyers(buyersRes.data);
      setVendors(vendorsRes.data);
      setBrokers(brokersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching filter options", error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      let endpoint = '';
      let params = new URLSearchParams();

      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.buyer) params.append('buyer', filters.buyer);
      if (filters.vendor) params.append('vendor', filters.vendor);
      if (filters.broker) params.append('broker', filters.broker);

      switch (selectedEntity) {
        case ENTITIES.SALES_ORDERS:
          endpoint = `${config.API_BASE_URL}/sales-orders?populate=true&populateItems=true&${params.toString()}`;
          break;
        case ENTITIES.PURCHASE_ORDERS:
          endpoint = `${config.API_BASE_URL}/purchase-orders?populate=true&populateItems=true&${params.toString()}`;
          break;
        case ENTITIES.TOTAL_INVENTORY:
        case ENTITIES.PRODUCT_STOCK_OVERVIEW:
          endpoint = `${config.API_BASE_URL}/inventory/stock-overview`;
          break;
        default:
          break;
      }

      const response = await axios.get(endpoint);
      
      // Some API responses are nested (like stock overview)
      if (selectedEntity === ENTITIES.TOTAL_INVENTORY || selectedEntity === ENTITIES.PRODUCT_STOCK_OVERVIEW) {
        setData(response.data.products || []);
        setReservedStockMap(response.data.reservedStock || {});
        setInProductionStockMap(response.data.inProductionStock || {});
      } else {
        setData(response.data || []);
      }

    } catch (error) {
      console.error("Error fetching report data", error);
      toast.error("Failed to fetch report data");
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableColumns = () => {
    switch (selectedEntity) {
      case ENTITIES.SALES_ORDERS:
        return [
          { header: 'SN', key: 'sn' },
          { header: 'Order Number', key: 'orderNumber' },
          { header: 'Order Date', key: 'orderDate' },
          { header: 'Buyer Name', key: 'buyer.business_name' },
          { header: 'Broker Name', key: 'broker.business_name' },
          { header: 'EDD', key: 'EDD' },
          { header: 'Product Name', key: 'items.product.productName' },
          { header: 'Order Quantity', key: 'items.quantity' },
          { header: 'Weight Type', key: 'items.grossOrNet' },
          { header: 'Custom Marking', key: 'items.customMarking' },
          { header: 'Packing Material', key: 'items.packingBagType.name' },
          { header: 'Unit Price', key: 'items.unitPrice' },
          { header: 'Base Amount', key: 'items.baseAmount' },
          { header: 'Tax', key: 'items.tax' },
          { header: 'Tax Amount', key: 'items.taxAmount' },
          { header: 'Total Item Amount', key: 'items.totalPrice' },
          { header: 'Quality Check', key: 'labTestRequired' },
          { header: 'Status', key: 'status' },
          { header: 'Transportation Amount', key: 'transportationAmount' },
          { header: 'Total SO Amount', key: 'totalAmount' },
          { header: 'Payment Status', key: 'paymentStatus' }
        ];
      case ENTITIES.PURCHASE_ORDERS:
        return [
          { header: 'SN', key: 'sn' },
          { header: 'PO Number', key: 'orderId' },
          { header: 'Order Date', key: 'orderDate' },
          { header: 'Vendor Name', key: 'vendorId.business_name' },
          { header: 'Broker Name', key: 'brokerId.business_name' },
          { header: 'Product', key: 'items.productId.productName' },
          { header: 'Quality', key: 'items.quality' },
          { header: 'Ordered Quantity', key: 'items.orderedQuantity' },
          { header: 'Unit Price', key: 'items.rate' },
          { header: 'Total Item Amount', key: 'items.totalPrice' },
          { header: 'Quality Check Qty', key: 'items.qualityCheckedQuantity' },
          { header: 'Unloaded Qty', key: 'items.unloadedQuantity' },
          { header: 'Returned Qty', key: 'items.returnedQuantity' },
          { header: 'Return Details', key: 'items.returnDetails' },
          { header: 'EDD', key: 'expectedDeliveryDate' },
          { header: 'Status', key: 'status' },
          { header: 'Items SubTotal', key: 'itemsTotalPrice' },
          { header: 'Tax (%)', key: 'tax' },
          { header: 'Grand Total', key: 'finalPrice' },
          { header: 'Payment Status', key: 'paymentStatus' }
        ];
      case ENTITIES.TOTAL_INVENTORY:
      case ENTITIES.PRODUCT_STOCK_OVERVIEW:
        return [
            { header: 'Product Name', key: 'product.productName' },
            { header: 'Product Group', key: 'product.groupId.groupName' },
            { header: 'Category', key: 'product.groupId.groupName' },
            { header: 'Total Available Stock', key: 'totalCombinedAvailable' },
            { header: 'Packed Stock', key: 'totalPacked' },
            { header: 'Unpacked Stock', key: 'totalUnpacked' },
            { header: 'In Packing Operations', key: 'totalInPacking' },
            { header: 'In Production', key: 'totalInProduction' },
            { header: 'Reserved Stock (for SOs)', key: 'reservedStock' },
            { header: 'Available After Reserved', key: 'availableAfterReserved' },
            { header: 'Min Stock (Buffer)', key: 'product.bufferStock' },
            { header: 'Stock Status', key: 'stockStatus' }
          ];
      default:
        return [];
    }
  };

  const getProcessedData = () => {
    const columns = getAvailableColumns();
    const flattened = [];
    let serialNumber = 1;

    if (selectedEntity === ENTITIES.TOTAL_INVENTORY) {
      data.forEach((item) => {
        const productId = item.product?._id;
        const totalPacked = (item.packedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
        const totalUnpacked = (item.unpackedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
        const totalInPacking = (item.inPackingStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
        const totalInProduction = inProductionStockMap[productId] || 0;
        const totalCombinedAvailable = totalPacked + totalUnpacked + totalInPacking;
        const reserved = reservedStockMap[productId] || 0;
        const availableAfterReserved = totalCombinedAvailable - reserved;
        const buffer = item.product?.bufferStock || 0;

        let stockStatus = 'In Stock';
        if (totalCombinedAvailable <= 0) stockStatus = 'Out of Stock';
        else if (totalCombinedAvailable <= buffer) stockStatus = 'Low Stock';

        // Filter by status if needed
        if (filters.status && stockStatus !== filters.status) return;

        const row = {};
        columns.forEach(col => {
          if (col.key === 'totalPacked') row[col.key] = totalPacked;
          else if (col.key === 'totalUnpacked') row[col.key] = totalUnpacked;
          else if (col.key === 'totalInPacking') row[col.key] = totalInPacking;
          else if (col.key === 'totalInProduction') row[col.key] = totalInProduction;
          else if (col.key === 'totalCombinedAvailable') row[col.key] = totalCombinedAvailable;
          else if (col.key === 'reservedStock') row[col.key] = reserved;
          else if (col.key === 'availableAfterReserved') row[col.key] = availableAfterReserved;
          else if (col.key === 'stockStatus') row[col.key] = stockStatus;
          else {
            row[col.key] = col.key.split('.').reduce((acc, part) => acc && acc[part], item) || '';
          }
        });
        flattened.push(row);
      });
      return flattened;
    }

    if (selectedEntity === ENTITIES.PRODUCT_STOCK_OVERVIEW) {
      if (!filters.product) return [];
      
      const item = data.find(p => p.product?._id === filters.product);
      if (!item) return [];

      const productId = item.product?._id;
      const totalPacked = (item.packedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
      const totalUnpacked = (item.unpackedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
      const totalInPacking = (item.inPackingStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
      const totalInProduction = inProductionStockMap[productId] || 0;
      const totalCombinedAvailable = totalPacked + totalUnpacked + totalInPacking;
      const reserved = reservedStockMap[productId] || 0;
      const availableAfterReserved = totalCombinedAvailable - reserved;
      const buffer = item.product?.bufferStock || 0;

      let stockStatus = 'In Stock';
      if (totalCombinedAvailable <= 0) stockStatus = 'Out of Stock';
      else if (totalCombinedAvailable <= buffer) stockStatus = 'Low Stock';

      // Row 1: Horizontal Summary (matches TOTAL_INVENTORY columns)
      const summaryRow = {};
      columns.forEach(col => {
        if (col.key === 'totalPacked') summaryRow[col.key] = totalPacked;
        else if (col.key === 'totalUnpacked') summaryRow[col.key] = totalUnpacked;
        else if (col.key === 'totalInPacking') summaryRow[col.key] = totalInPacking;
        else if (col.key === 'totalInProduction') summaryRow[col.key] = totalInProduction;
        else if (col.key === 'totalCombinedAvailable') summaryRow[col.key] = totalCombinedAvailable;
        else if (col.key === 'reservedStock') summaryRow[col.key] = reserved;
        else if (col.key === 'availableAfterReserved') summaryRow[col.key] = availableAfterReserved;
        else if (col.key === 'stockStatus') summaryRow[col.key] = stockStatus;
        else {
          summaryRow[col.key] = col.key.split('.').reduce((acc, part) => acc && acc[part], item) || '';
        }
      });
      summaryRow.rowType = 'SUMMARY';
      flattened.push(summaryRow);

      // Spacer
      flattened.push({ rowType: 'SPACER' });
      
      // Row 2: Breakdown Headers (mapped to summary columns)
      flattened.push({
        rowType: 'BREAKDOWN_HEADER',
        'product.productName': 'BREAKDOWN: PACKED STOCK',
        'product.groupId.groupName': 'Warehouse',
        'totalCombinedAvailable': 'Quantity',
        'totalPacked': 'Packing Material',
        'totalUnpacked': 'No of Bags',
        'totalInPacking': 'Outer Packing',
        'totalInProduction': 'Outer Bags'
      });

      // Breakdown Rows
      (item.packedStock || []).forEach(ps => {
        flattened.push({
          rowType: 'PACKED_ROW',
          'product.productName': '',
          'product.groupId.groupName': ps.warehouseLocation?.label || ps.warehouseLocation?.name,
          'totalCombinedAvailable': ps.availableStock,
          'totalPacked': ps.packingMaterial?.name,
          'totalUnpacked': ps.totalBags,
          'totalInPacking': ps.outerPackingMaterial?.name,
          'totalInProduction': ps.availableOuterBags
        });
      });

      return flattened;
    }

    data.forEach((item) => {
      // For Purchase Orders, calculate totals if they're not present
      let poSubtotal = item.itemsTotalPrice;
      let poGrandTotal = item.finalPrice;
      
      if (selectedEntity === ENTITIES.PURCHASE_ORDERS && item.items) {
        const calculatedSubtotal = item.items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0);
        if (!poSubtotal) poSubtotal = calculatedSubtotal;
        
        if (!poGrandTotal) {
          const taxPercent = parseFloat(item.tax) || 0;
          poGrandTotal = calculatedSubtotal + (calculatedSubtotal * (taxPercent / 100));
        }
      }

      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        item.items.forEach((subItem, index) => {
          const row = {};
          columns.forEach(col => {
            if (col.header === 'SN') {
              row[col.key] = index === 0 ? serialNumber : '';
            } else if (col.key.startsWith('items.')) {
              // Always show item-specific data
              const subKey = col.key.replace('items.', '');
              row[col.key] = subKey.split('.').reduce((acc, part) => acc && acc[part], subItem) || '';
            } else {
              // Special handling for calculated PO totals
              if (selectedEntity === ENTITIES.PURCHASE_ORDERS && index === 0) {
                if (col.key === 'itemsTotalPrice') {
                  row[col.key] = poSubtotal;
                  return;
                }
                if (col.key === 'finalPrice') {
                  row[col.key] = poGrandTotal;
                  return;
                }
              }

              // Only show order-level data on the first row of the group
              if (index === 0) {
                row[col.key] = col.key.split('.').reduce((acc, part) => acc && acc[part], item) || '';
              } else {
                row[col.key] = '';
              }
            }
          });
          flattened.push(row);
        });
        serialNumber++;
      } else {
        const row = {};
        columns.forEach(col => {
          if (col.header === 'SN') {
            row[col.key] = serialNumber++;
          } else {
            row[col.key] = col.key.split('.').reduce((acc, part) => acc && acc[part], item) || '';
          }
        });
        flattened.push(row);
      }
    });
    return flattened;
  };

  const processedData = getProcessedData();

  if (!canViewReports) {
    return (
      <Layout title="Reports">
        <Alert variant="danger">
          You don't have permission to view reports.
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout title="Reports">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Customizable Reports</li>
        </ol>
      </nav>

      <Row className="mb-4">
        <Col>
          <h2>Customizable Reports</h2>
          <p className="text-muted">Generate and download custom reports for various modules</p>
        </Col>
      </Row>

      <Card className="mb-4 shadow-sm border-light">
        <Card.Body>
          <Form>
            <Row className="gy-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Report Type</Form.Label>
                  <Form.Select 
                    value={selectedEntity} 
                    onChange={(e) => {
                      setSelectedEntity(e.target.value);
                      setData([]);
                    }}
                  >
                    {Object.values(ENTITIES).map(entity => (
                      <option key={entity} value={entity}>{entity}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {(selectedEntity === ENTITIES.SALES_ORDERS || selectedEntity === ENTITIES.PURCHASE_ORDERS) && (
              <>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    name="startDate" 
                    value={filters.startDate} 
                    onChange={handleFilterChange} 
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    name="endDate" 
                    value={filters.endDate} 
                    onChange={handleFilterChange} 
                  />
                </Form.Group>
              </Col>
              </>
              )}
              {selectedEntity != ENTITIES.PRODUCT_STOCK_OVERVIEW && (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select 
                    name="status" 
                    value={filters.status} 
                    onChange={handleFilterChange}
                  >
                    <option value="">All Statuses</option>
                    {selectedEntity === ENTITIES.SALES_ORDERS && (
                      <>
                        <option value="New">New</option>
                        <option value="Processing">Processing</option>
                        <option value="Dispatched">Dispatched</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </>
                    )}
                    {selectedEntity === ENTITIES.PURCHASE_ORDERS && (
                      <>
                        <option value="Draft">Draft</option>
                        <option value="Ordered">Ordered</option>
                        <option value="Received">Received</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </>
                    )}
                    {selectedEntity === ENTITIES.TOTAL_INVENTORY && (
                      <>
                        <option value="In Stock">In Stock</option>
                        <option value="Low Stock">Low Stock</option>
                        <option value="Out of Stock">Out of Stock</option>
                      </>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              )}

              {selectedEntity === ENTITIES.PRODUCT_STOCK_OVERVIEW && (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Select Product</Form.Label>
                    <Form.Select 
                      name="product" 
                      value={filters.product} 
                      onChange={handleFilterChange}
                    >
                      <option value="">Choose a product...</option>
                      {products.map(p => (
                        <option key={p._id} value={p._id}>{p.productName}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              {selectedEntity === ENTITIES.SALES_ORDERS && (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Buyer</Form.Label>
                    <Form.Select 
                      name="buyer" 
                      value={filters.buyer} 
                      onChange={handleFilterChange}
                    >
                      <option value="">All Buyers</option>
                      {buyers.map(b => (
                        <option key={b._id} value={b._id}>{b.companyName}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              {selectedEntity === ENTITIES.PURCHASE_ORDERS && (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Vendor</Form.Label>
                    <Form.Select 
                      name="vendor" 
                      value={filters.vendor} 
                      onChange={handleFilterChange}
                    >
                      <option value="">All Vendors</option>
                      {vendors.map(v => (
                        <option key={v._id} value={v._id}>{v.companyName}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              {selectedEntity === ENTITIES.PURCHASE_ORDERS && (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Broker</Form.Label>
                    <Form.Select 
                      name="broker" 
                      value={filters.broker} 
                      onChange={handleFilterChange}
                    >
                      <option value="">All Brokers</option>
                      {brokers.map(b => (
                        <option key={b._id} value={b._id}>{b.companyName}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              <Col md={selectedEntity === ENTITIES.TOTAL_INVENTORY || selectedEntity === ENTITIES.PRODUCT_STOCK_OVERVIEW ? 3 : 12} className="d-flex align-items-end justify-content-end">
                <Button 
                    variant="primary" 
                    className="w-100 w-md-auto" 
                    onClick={fetchReportData}
                    disabled={isLoading}
                >
                  {isLoading ? <PulseLoader color="#fff" size={6} /> : <><FiSearch className="me-2" /> Generate Report</>}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {data.length > 0 && (
        <Card className="shadow-sm border-light">
          <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Report Preview ({data.length} records found)</h5>
            <Button variant="outline-primary" size="sm" onClick={() => setShowExportModal(true)}>
              <FiDownload className="me-2" /> Download Options
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="align-middle mb-0 fs-9">
                <thead className="bg-light">
                  <tr>
                    {getAvailableColumns().map(col => (
                      <th key={col.key}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(selectedEntity === ENTITIES.PRODUCT_STOCK_OVERVIEW ? processedData : processedData.slice(0, 10)).map((item, idx) => (
                    <tr key={idx} className={item.rowType === 'SUMMARY' ? 'table-light' : item.rowType === 'BREAKDOWN' ? 'bg-secondary text-white' : ''}>
                      {getAvailableColumns().map(col => {
                        let val = item[col.key];
                        return <td key={col.key}>{formatValue(val, col.header, col.key)}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
              {data.length > 10 && (
                <div className="text-center p-3 text-muted">
                  Showing first 10 rows. Download to see full report.
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {data.length === 0 && !isLoading && (
          <div className="text-center py-5">
              <FiFileText size={48} className="text-muted mb-3" />
              <p className="text-muted">No data generated yet. Choose filters and click 'Generate Report'.</p>
          </div>
      )}

      <ReportDownloader 
        show={showExportModal} 
        handleClose={() => setShowExportModal(false)}
        data={(selectedEntity === ENTITIES.TOTAL_INVENTORY || selectedEntity === ENTITIES.PRODUCT_STOCK_OVERVIEW) ? processedData : data}
        availableColumns={getAvailableColumns()}
        title={`Export ${selectedEntity}`}
        fileName={selectedEntity.toLowerCase().replace(' ', '_')}
      />
    </Layout>
  );
};

export default Reports;
